import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import SecurityIcon from '@mui/icons-material/Security';
import WelcomeIcon from '@mui/icons-material/WavingHand';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const { account: user } = useAccount();
  const theme = useTheme();

  const handleCompleteSetup = (): void => {
    navigate('/company-info');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 'md',
          width: '100%',
          p: 4,
          textAlign: 'center',
          borderRadius: 2,
        }}
      >
        <Box sx={{ mb: 4 }}>
          <WelcomeIcon
            sx={{
              fontSize: 60,
              color: theme.palette.bidlogiq.orange,
              mb: 2,
            }}
          />
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              color: theme.palette.text.primary,
            }}
          >
            Welcome to BIDLOGIQ!
          </Typography>
          <Typography variant="h6" color="text.primary" sx={{ mb: 3 }}>
            Hi {user?.displayName || user?.email?.split('@')[0] || 'there'}! Let's get you set up
            for success.
          </Typography>
        </Box>

        <Card
          sx={{
            mb: 4,
            backgroundColor: `${theme.palette.bidlogiq.taupe}10`,
            border: `1px solid ${theme.palette.bidlogiq.orange}30`,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <InfoIcon sx={{ color: theme.palette.bidlogiq.orange, mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Before you start adding tenders...
              </Typography>
            </Box>
            <Typography variant="body1" color="text.primary" sx={{ textAlign: 'left' }}>
              To get the best results from BIDLOGIQ's AI-powered tender responses, please complete
              these quick setup steps:
            </Typography>
          </CardContent>
        </Card>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `2px solid ${theme.palette.bidlogiq.orange}`,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BusinessIcon
                    sx={{
                      fontSize: 40,
                      color: theme.palette.bidlogiq.orange,
                      mr: 2,
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Step 1: Company Info
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.primary" sx={{ mb: 2, textAlign: 'left' }}>
                  Note: You must fill in your company details to complete the sign-up process.
                </Typography>
                <List dense>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 20 }}>
                      <CheckCircleIcon
                        sx={{
                          fontSize: 16,
                          color: theme.palette.success.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Company details"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 20 }}>
                      <CheckCircleIcon
                        sx={{
                          fontSize: 16,
                          color: theme.palette.success.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Insurance information"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 20 }}>
                      <CheckCircleIcon
                        sx={{
                          fontSize: 16,
                          color: theme.palette.success.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Legal entity details"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `2px solid ${theme.palette.bidlogiq.orange}`,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SecurityIcon
                    sx={{
                      fontSize: 40,
                      color: theme.palette.bidlogiq.orange,
                      mr: 2,
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Step 2: Secure Your Account
                  </Typography>
                </Box>
                <List dense>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 20 }}>
                      <CheckCircleIcon
                        sx={{
                          fontSize: 16,
                          color: theme.palette.success.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Enable Multi-Factor Authentication (MFA) via the My Account page to keep your account secure."
                      primaryTypographyProps={{ fontSize: '0.9rem', sx: { textAlign: 'left' } }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `2px solid ${theme.palette.bidlogiq.orange}`,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccountCircleIcon
                    sx={{
                      fontSize: 40,
                      color: theme.palette.bidlogiq.orange,
                      mr: 2,
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Step 3: Storage Options
                  </Typography>
                </Box>
                <List dense>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 20 }}>
                      <CheckCircleIcon
                        sx={{
                          fontSize: 16,
                          color: theme.palette.success.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="(Optional) Connect to your own SharePoint site for document storage. This can be configured from the My Account page."
                      primaryTypographyProps={{ fontSize: '0.9rem', sx: { textAlign: 'left' } }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Button
          variant="contained"
          onClick={handleCompleteSetup}
          endIcon={<ArrowForwardIcon />}
          sx={{
            backgroundColor: theme.palette.bidlogiq.brick,
            color: theme.palette.bidlogiq.white,
            '&:hover': {
              backgroundColor: theme.palette.bidlogiq.brick,
              opacity: 0.9,
            },
          }}
        >
          Continue to Setup
        </Button>
        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" color="text.primary">
          You can complete these steps at any time from your Company Info and My Account pages.
        </Typography>
      </Paper>
    </Box>
  );
};

export default WelcomePage;
